/**
 * ReviewBadges — status/verdict/trigger chips shared by ReviewsListPage and
 * ReviewDetailPage so the two surfaces render identical badges.
 *
 * Verdict badge uses MUI icons (not raw emoji) per ui-ux-pro-max's
 * "don't emoji-as-icon" rule — the ✅/⚠️/🛑 semantics the P10 brief asked
 * for are preserved via icon shape + the standard success/warning/error
 * palette colors instead.
 */

import type { ReactElement } from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import type { ReviewStatus, ReviewTrigger, Verdict } from '@/types/review';

export function ReviewStatusChip({
  status,
  superseded = false,
}: {
  status: ReviewStatus;
  superseded?: boolean;
}): ReactElement {
  const map: Record<
    ReviewStatus,
    { color: 'default' | 'success' | 'error' | 'warning'; label: string }
  > = {
    queued: { color: 'default', label: 'queued' },
    running: { color: 'warning', label: 'running' },
    done: { color: 'success', label: 'done' },
    error: { color: 'error', label: 'error' },
    skipped: { color: 'default', label: 'skipped' },
  };
  const { color, label } = map[status];
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Chip size="small" color={color} label={label} variant="outlined" />
      {superseded && (
        <Tooltip title="A newer push superseded this review before it finished">
          <Chip size="small" color="default" label="superseded" variant="outlined" />
        </Tooltip>
      )}
    </Stack>
  );
}

export function ReviewVerdictBadge({ verdict }: { verdict: Verdict | null }): ReactElement {
  if (verdict === null) {
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<HourglassEmptyIcon fontSize="small" />}
        label="pending"
        aria-label="verdict: pending"
        sx={{ color: 'text.secondary', borderColor: 'divider' }}
      />
    );
  }
  const map: Record<
    Verdict,
    { color: 'success' | 'warning' | 'error'; label: string; icon: ReactElement }
  > = {
    clean: { color: 'success', label: 'clean', icon: <CheckCircleOutlineIcon fontSize="small" /> },
    concerns: { color: 'warning', label: 'concerns', icon: <WarningAmberIcon fontSize="small" /> },
    serious: { color: 'error', label: 'serious', icon: <ErrorOutlineIcon fontSize="small" /> },
  };
  const { color, label, icon } = map[verdict];
  return (
    <Chip
      size="small"
      color={color}
      variant="filled"
      icon={icon}
      label={label}
      aria-label={`verdict: ${label}`}
    />
  );
}

export function ReviewTriggerChip({ trigger }: { trigger: ReviewTrigger }): ReactElement {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={trigger}
      sx={{ fontFamily: 'monospace', fontSize: '0.6875rem' }}
    />
  );
}
